// 🔧 use_promotion_settings.js
// ✅ Purpose: React hook for accessing and auto-initializing Promotion Engine configuration
// ✅ Features:
// - Loads Promotion Engine settings from Firestore
// - Automatically creates promotion_settings/main if missing
// - Restricts private reads/initialization to global admins; event managers use safe callable flags
// - Falls back to engine defaults while loading or if initialization fails
// - Prevents manual Firestore seeding for new environments
// - Exposes loading state
// - Future-ready for permissions, feature flags, providers, and connected accounts
// - Fully block-commented for clarity and future devs

import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { useRole } from "../../role_context";
import { useEventContext } from "../../hooks/use_event_context";

import {
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import {
  getPromotionSettingsRef,
} from "../services/promotion_repository";

import {
  PROMOTION_DEFAULTS,
} from "../config/promotion_defaults";

const DISABLED_EVENT_SETTINGS = { enabled: false, aiEnabled: false, imageGenerationEnabled: false, schedulerEnabled: false };

/**
 * ============================================================================
 * usePromotionSettings
 * ============================================================================
 */

export const usePromotionSettings = () => {
  const { role, roleLoading } = useRole();
  const { eventId, eventLoading } = useEventContext({ redirectIfNotFound: false });
  const [settings, setSettings] = useState(
    role === "admin" ? PROMOTION_DEFAULTS : DISABLED_EVENT_SETTINGS
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    if (roleLoading || (role !== "admin" && eventLoading)) {
      setSettings(DISABLED_EVENT_SETTINGS);
      return () => { mounted = false; };
    }

    // Event managers cannot read or initialize the private configuration.
    // The authorized metadata callable returns only safe workflow switches.
    if (role !== "admin") {
      setSettings(DISABLED_EVENT_SETTINGS);
      if (!eventId) {
        setLoading(false);
        return () => { mounted = false; };
      }
      httpsCallable(functions, "getPromotionAiUsage")({ eventId })
        .then((response) => {
          if (!mounted) return;
          const safe = response?.data?.settings || {};
          setSettings({
            enabled: safe.enabled === true,
            aiEnabled: safe.aiEnabled === true,
            imageGenerationEnabled: safe.imageGenerationEnabled === true,
            schedulerEnabled: safe.schedulerEnabled === true,
          });
        })
        .catch((loadError) => {
          if (!mounted) return;
          console.warn("[Promotion Engine] Event settings are unavailable", { code: loadError?.code || "unknown" });
          setSettings(DISABLED_EVENT_SETTINGS);
          setError(loadError);
        })
        .finally(() => { if (mounted) setLoading(false); });
      return () => { mounted = false; };
    }

    /**
     * ------------------------------------------------------------------------
     * loadSettings
     * ------------------------------------------------------------------------
     * Purpose:
     * - Read Promotion Engine settings from Firestore
     * - If the settings document does not exist, create it automatically
     * - This prevents manual seeding in DEV, TEST, or PROD
     */

    const loadSettings = async () => {
      try {
        const settingsRef =
          getPromotionSettingsRef();

        const snapshot =
          await getDoc(settingsRef);

        /**
         * --------------------------------------------------------------------
         * Existing Settings
         * --------------------------------------------------------------------
         */

        if (snapshot.exists()) {
          if (mounted) {
            setSettings({
              ...PROMOTION_DEFAULTS,
              ...snapshot.data(),
            });
          }

          return;
        }

        /**
         * --------------------------------------------------------------------
         * Auto Initialize Settings
         * --------------------------------------------------------------------
         * If promotion_settings/main is missing, create it once using defaults.
         */

        const initialSettings = {
          ...PROMOTION_DEFAULTS,

          initialized:
            true,

          initializedBy:
            "promotion_engine",

          createdAt:
            serverTimestamp(),

          updatedAt:
            serverTimestamp(),
        };

        await setDoc(
          settingsRef,
          initialSettings,
          {
            merge: true,
          }
        );

        if (mounted) {
          setSettings({
            ...PROMOTION_DEFAULTS,
            ...initialSettings,
          });
        }
      } catch (error) {
        console.warn(
          "[Promotion Engine] Failed loading or initializing settings:",
          error
        );

        /**
         * --------------------------------------------------------------------
         * Safe Fallback
         * --------------------------------------------------------------------
         * The page can still render using local defaults even if Firestore fails.
         */

        if (mounted) {
          setSettings(PROMOTION_DEFAULTS);
          setError(error);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      mounted = false;
    };
  }, [role, roleLoading, eventId, eventLoading]);

  return {
    loading,
    settings,
    error,

    /**
     * Future expansion
     */

    featureFlags: {},
    providers: {},
    permissions: {},
    connectedPlatforms: [],
  };
};
